import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import useUserStore from "../../stores/user";
import supabase from "../../utils/supabase";
import { toast } from "react-toastify";

interface ExportAnalyticsReportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface School {
  id: string;
  name: string;
}

interface Class {
  id: string;
  name: string;
}

type ExportScope = "district" | "school" | "class";
type ExportFormat = "pdf" | "csv";

const ExportAnalyticsReportModal: React.FC<ExportAnalyticsReportModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { profile } = useUserStore();
  const [exportScope, setExportScope] = useState<ExportScope>("district");
  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf");
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [schools, setSchools] = useState<School[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);

  // Fetch schools when modal opens and profile is available
  useEffect(() => {
    if (isOpen && profile?.district) {
      fetchSchools();
    }
  }, [isOpen, profile?.district]);

  // Fetch classes when schools are loaded
  useEffect(() => {
    if (isOpen && profile?.district) {
      fetchClasses();
    }
  }, [isOpen, profile?.district]);

  // Reset report URL when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReportUrl(null);
    }
  }, [isOpen]);

  // Reset report URL when export options change
  useEffect(() => {
    if (reportUrl) {
      setReportUrl(null);
    }
  }, [exportScope, exportFormat, selectedSchool, selectedClass]);

  const fetchSchools = async () => {
    if (!profile?.district) return;

    setLoadingSchools(true);
    try {
      const { data, error } = await supabase
        .from("schools")
        .select("id, name")
        .eq("district_id", profile.district);

      if (error) {
        console.error("Error fetching schools:", error);
        toast.error("Failed to fetch schools");
        return;
      }

      setSchools(data || []);
    } catch (error) {
      console.error("Error fetching schools:", error);
      toast.error("Failed to fetch schools");
    } finally {
      setLoadingSchools(false);
    }
  };

  const fetchClasses = async () => {
    if (!profile?.district) return;

    setLoadingClasses(true);
    try {
      const { data, error } = await supabase
        .from("classes")
        .select("id, name")
        .eq("district_id", profile.district);

      if (error) {
        console.error("Error fetching classes:", error);
        toast.error("Failed to fetch classes");
        return;
      }

      setClasses(data || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      toast.error("Failed to fetch classes");
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleExport = async () => {
    if (!profile?.district) {
      toast.error("District information not found");
      return;
    }

    setLoading(true);
    try {
      const exportData = {
        scope: exportScope,
        format: exportFormat,
        districtId: profile.district,
        schoolId: exportScope === "school" ? selectedSchool : null,
        classId: exportScope === "class" ? selectedClass : null,
      };

      const { data, error } = await supabase.functions.invoke(
        "exportAnalyticsReport",
        {
          body: exportData,
        }
      );

      if (error) {
        console.error("Error exporting report:", error);
        toast.error("Failed to export report");
        return;
      }

      if (data?.reportUrl) {
        setReportUrl(data.reportUrl);
        toast.success("Report exported successfully!");
      }
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to export report");
    } finally {
      setLoading(false);
    }
  };

  const canExport = () => {
    if (exportScope === "school" && !selectedSchool) return false;
    if (exportScope === "class" && !selectedClass) return false;
    return true;
  };

  const handleDownload = () => {
    if (reportUrl) {
      const link = document.createElement("a");
      link.href = reportUrl;
      link.download = `analytics_report.${exportFormat}`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleTryAgain = () => {
    setReportUrl(null);
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Analytics Report">
      <div className="space-y-6">
        {/* Export Scope Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Export Scope
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="district"
                checked={exportScope === "district"}
                onChange={(e) => setExportScope(e.target.value as ExportScope)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">
                District (All schools and classes)
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="school"
                checked={exportScope === "school"}
                onChange={(e) => setExportScope(e.target.value as ExportScope)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">School</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="class"
                checked={exportScope === "class"}
                onChange={(e) => setExportScope(e.target.value as ExportScope)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Class</span>
            </label>
          </div>
        </div>

        {/* School Selection (when school scope is selected) */}
        {exportScope === "school" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select School
            </label>
            <select
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loadingSchools}
            >
              <option value="">Select a school...</option>
              {schools.map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
            {loadingSchools && (
              <p className="text-sm text-gray-500 mt-1">Loading schools...</p>
            )}
          </div>
        )}

        {/* Class Selection (when class scope is selected) */}
        {exportScope === "class" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Class
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              disabled={loadingClasses}
            >
              <option value="">Select a class...</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.id}>
                  {cls.name}
                </option>
              ))}
            </select>
            {loadingClasses && (
              <p className="text-sm text-gray-500 mt-1">Loading classes...</p>
            )}
          </div>
        )}

        {/* Export Format Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Export Format
          </label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="pdf"
                checked={exportFormat === "pdf"}
                onChange={(e) =>
                  setExportFormat(e.target.value as ExportFormat)
                }
                className="mr-2"
              />
              <span className="text-sm text-gray-700">PDF Report</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="csv"
                checked={exportFormat === "csv"}
                onChange={(e) =>
                  setExportFormat(e.target.value as ExportFormat)
                }
                className="mr-2"
              />
              <span className="text-sm text-gray-700">CSV Data Export</span>
            </label>
          </div>
        </div>

        {/* Success Message and Download Button */}
        {reportUrl && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Report generated successfully!
                </p>
                <p className="text-sm text-green-700 mt-1">
                  Your {exportFormat.toUpperCase()} report is ready for
                  download.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Export Button */}
        <div className="flex justify-end space-x-3 pt-4">
          {reportUrl ? (
            <>
              <button
                onClick={handleTryAgain}
                className="btn btn-small btn-secondary"
              >
                Try Again
              </button>
              <button onClick={handleDownload} className="btn btn-small">
                Download Report
              </button>
            </>
          ) : (
            <button
              onClick={handleExport}
              disabled={!canExport() || loading}
              className="btn btn-small btn-primary"
            >
              {loading ? "Exporting..." : "Export Report"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ExportAnalyticsReportModal;
